import { useMutation, useQuery } from '@tanstack/react-query';
import { Loading, Navbar } from 'components';
import { COOKIE_KEYS, queryKeys, routes, storageKeys } from '../constants';
import { Outlet, useMatch, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import {
  getActivityAreas,
  getFemaleRanking,
  getFilterOptions,
  getListPendingPayment,
  getPoints,
  sendActive,
} from 'api';
import { useAppStore, usePointStore, useUserStore } from 'store';
import {
  ERankingPeriod,
  ERankingProjectType,
  EUserPlatform,
  TFilterOptions,
  TPoints,
  TSendActive,
} from 'types';
import { createPubNubClient } from 'configs/pubnubClient';
import { PubNubProvider } from 'pubnub-react';
import { getMessagingToken } from 'configs';
import { useABTesting, useMessage } from 'hooks';
import OneSignal from 'react-onesignal';
import { detectBrowserVersion, detectPWA, getCookies, setCookie } from 'utils';

const isPWA = detectPWA();
const browserVersion = detectBrowserVersion();

type Props = {
  loading: boolean;
};

function Layout({ loading }: Props) {
  // Hooks
  const isInitial = useRef(true);
  const { isAuthenticated, user, setClientId, subscriptionId } = useUserStore();
  const {
    setFilterOptions,
    setPaymentPending,
    setActivityAreas,
    setOverallRankings,
  } = useAppStore();
  const { setPoints } = usePointStore();
  const isInConversation = useMatch(routes.CONVERSATION);
  const isInYarikanoChat = useMatch(routes.YARIKANO_CHAT);
  const isInChat = isInConversation || isInYarikanoChat;
  const navigate = useNavigate();
  const { refetch: refetchUnreadCount } = useMessage({ enabled: true });
  const { handleAccessApp } = useABTesting();

  // Queries
  const { data: points } = useQuery<TPoints>({
    queryKey: [queryKeys.POINTS],
    queryFn: getPoints,
    enabled: isAuthenticated,
  });

  const { data: filterOptions } = useQuery<TFilterOptions>({
    refetchOnMount: false,
    queryKey: [queryKeys.FILTER_OPTIONS],
    queryFn: getFilterOptions,
  });

  const { data: listPendingPayment = [], dataUpdatedAt } = useQuery<string[]>({
    queryKey: [queryKeys.LIST_PENDING_PAYMENT],
    queryFn: getListPendingPayment,
    enabled: isAuthenticated,
  });

  const { data: activityAreas = [] } = useQuery({
    queryKey: [queryKeys.ACTIVITY_AREA],
    queryFn: () => getActivityAreas(),
    refetchOnMount: false,
  });

  const { data: overallRankings = [] } = useQuery({
    queryKey: [queryKeys.RANKING],
    queryFn: async () => {
      const [groupRankings, individualRankings] = await Promise.all([
        getFemaleRanking({
          projectType: ERankingProjectType.GROUP,
          period: ERankingPeriod.YESTERDAY,
          areaId: undefined,
        }),
        getFemaleRanking({
          projectType: ERankingProjectType.INDIVIDUAL,
          period: ERankingPeriod.YESTERDAY,
          areaId: undefined,
        }),
      ]);

      const rankingsByFemale = new Map<
        number,
        (typeof groupRankings)[number]
      >();
      for (const ranking of [...groupRankings, ...individualRankings]) {
        const current = rankingsByFemale.get(ranking.user.id);
        if (!current || ranking.rank < current.rank) {
          rankingsByFemale.set(ranking.user.id, ranking);
        }
      }

      return Array.from(rankingsByFemale.values());
    },
  });

  // Mutations
  const { mutateAsync: mutateSendActive } = useMutation({
    mutationFn: (body: TSendActive) => {
      return sendActive(body);
    },
  });

  //Effects
  useEffect(() => {
    if (!isInitial.current) {
      refetchUnreadCount();
      handleAccessApp();
    } else {
      isInitial.current = false;
    }

    return () => {};
  }, [isAuthenticated, refetchUnreadCount, handleAccessApp]);

  useEffect(() => {
    if (filterOptions) {
      setFilterOptions(filterOptions);
    }
  }, [filterOptions, setFilterOptions]);

  useEffect(() => {
    if (points) {
      setPoints(points);
    }
  }, [points, setPoints]);

  useEffect(() => {
    if (listPendingPayment.length) {
      setPaymentPending({
        ids: listPendingPayment,
        lastFetchingTime: dataUpdatedAt,
      });
    }
  }, [dataUpdatedAt, listPendingPayment, setPaymentPending]);

  useEffect(() => {
    if (activityAreas.length) {
      setActivityAreas(activityAreas);
    }
  }, [activityAreas, setActivityAreas]);

  useEffect(() => {
    if (overallRankings.length) {
      setOverallRankings(overallRankings);
    }
  }, [overallRankings, setOverallRankings]);

  // Memo, callbacks
  const clientId = useMemo(() => {
    const clientId =
      getCookies(COOKIE_KEYS.CLIENT_ID) ||
      localStorage.getItem(storageKeys.CLIENT_ID) ||
      `${Date.now()}`;
    setCookie([
      {
        key: COOKIE_KEYS.CLIENT_ID,
        value: clientId,
      },
    ]);
    setClientId(clientId);
    return clientId;
  }, [setClientId]);

  const pubnub = useMemo(() => {
    return createPubNubClient({
      publishKey: process.env.REACT_APP_PUBNUB_PUBLISH_KEY,
      subscribeKey: process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY || '',
      uuid: user?.uuid || clientId,
    });
  }, [user?.uuid, clientId]);

  useEffect(() => {
    if (mutateSendActive && user?.id && isAuthenticated) {
      (async () => {
        const token = await getMessagingToken();
        const oneSignalSubscriptionId =
          process.env.NODE_ENV !== 'production' &&
          process.env.REACT_APP_TECO_LOCAL_MODE === 'true'
            ? null
            : OneSignal.User?.PushSubscription?.id || null;
        await mutateSendActive({
          clientId,
          fcmToken: token || '',
          subscriptionId:
            process.env.NODE_ENV !== 'production' &&
            process.env.REACT_APP_TECO_LOCAL_MODE === 'true'
              ? subscriptionId
              : subscriptionId || oneSignalSubscriptionId,
          platform: isPWA ? EUserPlatform.APP : EUserPlatform.WEB,
          browserVersion: `${browserVersion}`,
        });
      })();
    }
  }, [clientId, user?.id, isAuthenticated, mutateSendActive, subscriptionId]);

  useEffect(() => {
    return () => {
      if (
        !!localStorage.getItem(storageKeys.REQUIRE_SIGNUP) &&
        !isAuthenticated &&
        ![routes.SIGNUP, routes.LOGIN].includes(window.location.pathname)
      ) {
        navigate(routes.SIGNUP);
      }
    };
  }, [navigate, isAuthenticated]);

  return loading ? (
    <Loading size="md" fullScreen />
  ) : (
    <div className="h-full w-full overflow-hidden">
      {pubnub && (
        <PubNubProvider client={pubnub}>
          <div className={`h-full ${!isInChat ? 'pb-[68px]' : ''}`}>
            <Outlet />
          </div>
          {!isInChat && <Navbar />}
        </PubNubProvider>
      )}
    </div>
  );
}

export default Layout;
export { SplashScreen } from './SplashScreen';
